import FormattedCurrency from '../common/FormattedCurrency';
import FullBlockingModal from '../common/FullBlockingModal';
import { useTranslation } from 'react-i18next';
import { Button } from '../common/Button';
import CollapsibleContent from '../common/CollapsibleContent';
import { Quote, ChildQuote, QuoteJob } from '../../types/quote';
import { computeQuoteTotalAmount } from '../../utils/jobUtils';
import { useProfileStore } from '../../stores/profileStore';
import { useQuoteStore } from '../../stores/quoteStore';
import { QuoteJobInfo } from './QuoteJobInfo';
import { QuoteDetailsInfo } from './QuoteDetailsInfo';
import { Job } from '../../types/job';
import { isQuoteActive } from '../../utils/quoteUtils';
import UpsertQuoteModal from './UpsertQuoteModal';
import { RequestChangeQuoteModal } from './RequestChangeQuoteModal';
import { useState } from 'react';

export default function SingleSubmissionModal({
  onClose,
  job,
  quote,
  expanded = false,
  parentQuote,
}: {
  onClose: () => void;
  job: QuoteJob | Job;
  quote: Quote | ChildQuote;
  expanded?: boolean;
  parentQuote?: Quote; // For alliance quotes, this is the parent quote
}) {
  const { t } = useTranslation(['quotes', 'freelancers', 'common', 'jobs', 'alliances']);
  const [showRequestChangeModal, setShowRequestChangeModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  const { profile } = useProfileStore();

  const freshFromStore = useQuoteStore(s => ({
    quote: s.quotes.find(q => String(q.id) === String(quote.id)),
    parent: parentQuote ? s.quotes.find(q => String(q.id) === String(parentQuote.id)) : undefined,
  }));
  const displayQuote = freshFromStore.quote ?? quote;
  const displayParentQuote = parentQuote ? (freshFromStore.parent ?? parentQuote) : undefined;

  const isOwner = profile?.id === displayQuote.quote_owner.id;
  const hasChangeRequested =
    'change_request_message' in displayQuote && !!displayQuote.change_request_message?.trim();

  // Calculate amounts using utility functions
  const totalAmount = computeQuoteTotalAmount(displayQuote);

  const handleRequestChange = () => {
    setShowRequestChangeModal(true);
  };

  const handleCloseRequestChangeModal = () => {
    setShowRequestChangeModal(false);
  };

  const isEditable = isOwner && 'job' in displayQuote && isQuoteActive(displayQuote as Quote);

  if (showEditModal && isEditable && 'job' in displayQuote) {
    return (
      <UpsertQuoteModal
        job={(displayQuote as Quote).job}
        quote={displayQuote as Quote}
        onClose={() => {
          setShowEditModal(false);
          onClose();
        }}
      />
    );
  }

  return (
    <FullBlockingModal innerClassName="md:max-w-[70rem]">
      <button
        className="absolute -top-1 right-1 text-gray-400 hover:text-gray-600 text-2xl md:top-0 md:right-4"
        onClick={onClose}
        aria-label={t('common:form_actions.close')}
      >
        &times;
      </button>

      {isOwner ? (
        <h2 className="text-secondary text-2xl font-bold">
          {t('addOfferSectionTitle', { ns: 'quotes', defaultValue: 'Mon offre' })}
        </h2>
      ) : (
        <h2 className="text-secondary text-2xl font-bold">
          {t('alliance_offer', { ns: 'quotes', defaultValue: 'L’offre de votre Alliance' })}
        </h2>
      )}

      <CollapsibleContent
        className="bg-white flex flex-col transition-all duration-300 mt-6"
        header={<QuoteJobInfo quoteJob={job} headerOnly={true} />}
        defaultExpanded={expanded}
        contentClassName="bg-white"
        headerClassName="items-start"
      >
        <QuoteJobInfo quoteJob={job} detailsOnly={true} />
      </CollapsibleContent>

      <hr className="border-[#E1E9F0] my-3" />

      {!isOwner && (
        <>
          <CollapsibleContent
            className="bg-white flex flex-col transition-all duration-300"
            header={
              <h3 className="text-lg font-bold text-secondary my-3">
                {t('alliance', { ns: 'alliances' })}
              </h3>
            }
            defaultExpanded={false}
            contentClassName="bg-white"
            headerClassName="relative items-center"
            expandButtonClassName="absolute top-0 md:static self-start md:self-center"
          >
            <QuoteDetailsInfo quote={displayQuote} job={job} headerOnly={true} variant="quote" />
          </CollapsibleContent>
          <hr className="border-[#E1E9F0] my-3" />
        </>
      )}

      <QuoteDetailsInfo
        quote={displayQuote}
        job={job}
        variant="quote"
        detailsOnly={true}
        title={
          isOwner
            ? t('my_submission', { ns: 'quotes' })
            : t('alliance_submission', { ns: 'quotes' })
        }
      />

      {totalAmount > 0 && (
        <div className="mt-4">
          <h4 className="text-secondary font-bold text-lg">
            {t('total_estimation', { ns: 'quotes' })}
          </h4>
          <span className="text-xl text-primary font-bold">
            <FormattedCurrency value={totalAmount} />
          </span>
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-col flex-wrap gap-4 mt-8 flex-col-reverse md:flex-row-reverse md:items-center md:mt-4">
        <Button
          type="button"
          onClick={onClose}
          className="w-full md:w-auto md:px-8"
          variant="primary"
        >
          {t('back_to_quotes', { ns: 'quotes' })}
        </Button>

        {isEditable && (
          <Button
            type="button"
            onClick={() => setShowEditModal(true)}
            className="w-full md:w-auto md:px-8"
            variant="transparent-with-border"
          >
            {t('modify', { ns: 'quotes', defaultValue: 'Modifier' })}
          </Button>
        )}

        {displayParentQuote &&
          isQuoteActive(displayParentQuote) &&
          !isOwner &&
          (hasChangeRequested ? (
            <Button
              type="button"
              disabled
              className="w-full md:w-auto md:px-8"
              variant="transparent-with-border"
            >
              {t('change_has_been_requested', { ns: 'quotes' })}
            </Button>
          ) : (
            <Button
              type="button"
              onClick={handleRequestChange}
              className="w-full md:w-auto md:px-8"
              variant="transparent-with-border"
            >
              {t('request_changes', { ns: 'quotes' })}
            </Button>
          ))}
      </div>

      {/* Request Change Modal */}
      {showRequestChangeModal && (
        <RequestChangeQuoteModal
          parentQuote={displayParentQuote ?? displayQuote}
          freelancerQuote={displayQuote}
          onClose={handleCloseRequestChangeModal}
        />
      )}
    </FullBlockingModal>
  );
}
